import { lazy, Suspense, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, WifiOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { AdsConsentBanner } from '@/components/privacy/AdsConsent';
import { trackEvent } from '@/ads';
import { useApp } from '@/app/AppProvider';
import { HomePage } from '@/pages/HomePage';
import { ProfileLandingPage } from '@/pages/ProfileLandingPage';

const ExamPage = lazy(() => import('@/pages/ExamPage').then((m) => ({ default: m.ExamPage })));
const AuthPage = lazy(() => import('@/pages/AuthPage').then((m) => ({ default: m.AuthPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const StudyPage = lazy(() => import('@/pages/StudyPage').then((m) => ({ default: m.StudyPage })));
const FleetPage = lazy(() => import('@/pages/FleetPage').then((m) => ({ default: m.FleetPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const PurchasePage = lazy(() => import('@/pages/PurchasePage').then((m) => ({ default: m.PurchasePage })));
const InvitationPage = lazy(() => import('@/pages/InvitationPage').then((m) => ({ default: m.InvitationPage })));
const VideosPage = lazy(() => import('@/pages/VideosPage').then((m) => ({ default: m.VideosPage })));
const PUBLIC_URL = 'https://permisovtc.es';

const META: Record<string, { title: string; description: string; index?: boolean }> = {
  '/': {
    title: 'Permiso VTC Madrid — Simulador del examen oficial',
    description: 'Simulador de 60 preguntas, estudio por módulos y seguimiento para preparar la prueba de conductor VTC de Madrid.',
  },
  '/simulacro': { title: 'Simulacro VTC Madrid · 60 preguntas', description: 'Reproduce la estructura del examen VTC de Madrid y recibe el resultado separado de los cuatro módulos.' },
  '/estudio': { title: 'Estudiar el examen VTC por módulos', description: 'Practica el temario VTC de Madrid en tandas breves con corrección inmediata y repaso de fallos.' },
  '/precios': { title: 'Planes y precios · Permiso VTC Madrid', description: 'Acceso individual y licencias para flotas, con pago único y sin suscripción.' },
  '/videos': { title: 'Vídeos del panel · Permiso VTC Madrid', description: 'Videoteca explicativa por módulos para preparar la prueba de conductor VTC.', index: false },
  '/conductores': { title: 'Preparación VTC para conductores en Madrid', description: 'Simulacros y estudio breve desde el móvil para preparar los cuatro módulos del examen VTC.' },
  '/autonomos': { title: 'Preparación VTC para autónomos · Pago único', description: 'Prepara el permiso VTC a tu horario, sin cuotas mensuales.' },
  '/flotas': { title: 'Preparación del permiso VTC para flotas', description: 'Licencias, invitaciones y seguimiento consentido para empresas VTC.' },
  '/academias': { title: 'Plataforma VTC para academias', description: 'Práctica por módulos, simulacros y seguimiento para grupos de alumnos VTC.' },
};

function LoadingPage() {
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6">
      <Skeleton className="h-10 w-56" />
      <Skeleton className="mt-5 h-24 w-full" />
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Skeleton className="h-44" />
        <Skeleton className="h-44" />
        <Skeleton className="h-44" />
      </div>
    </div>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const { online } = useApp();
  const location = useLocation();
  const compact = location.pathname === '/simulacro';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader compact={compact} />
      {!online && (
        <Alert className="mx-auto mt-3 max-w-6xl rounded-none border-x-0 border-warning/40 bg-warning/10 sm:rounded-xl sm:border-x">
          <WifiOff />
          <AlertTitle>Estás sin conexión</AlertTitle>
          <AlertDescription>El intento actual se conserva. Volveremos a sincronizar cuando recuperes internet.</AlertDescription>
        </Alert>
      )}
      <main>{children}</main>
      {!compact && <SiteFooter />}
      <AdsConsentBanner />
    </div>
  );
}

function NotFoundPage() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-24 text-center">
      <AlertTriangle className="mx-auto size-10 text-warning" />
      <h1 className="mt-5 text-3xl font-semibold tracking-tight">Esta página no existe</h1>
      <p className="mt-3 text-muted-foreground">Puedes volver al simulador o consultar cómo es la prueba.</p>
      <a className="mt-6 inline-flex h-11 items-center rounded-lg bg-primary px-5 font-medium text-primary-foreground" href="/">
        Volver al inicio
      </a>
    </section>
  );
}

export function App() {
  const location = useLocation();
  const path = location.pathname !== '/' ? location.pathname.replace(/\/+$/, '') : '/';
  useEffect(() => {
    const meta = META[path] ?? {
      title: 'Permiso VTC Madrid',
      description: 'Preparación privada para la prueba de conductor VTC de la Comunidad de Madrid.',
      index: false,
    };
    document.title = meta.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = meta.description;
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${PUBLIC_URL}${path === '/' ? '/' : path}`;
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = meta.index === false ? 'noindex, follow' : 'index, follow';
    // La etiqueta solo envía una vista de página al cargar. En una SPA el resto
    // de la navegación es interna, así que sin esto GA4 vería una única página
    // por sesión y el embudo quedaría plano.
    trackEvent('page_view', {
      page_path: path,
      page_title: meta.title,
      page_location: `${PUBLIC_URL}${path === '/' ? '/' : path}`,
    });
  }, [path]);
  let page: React.ReactNode;

  if (path === '/') page = <HomePage />;
  else if (path === '/simulacro') page = <ExamPage />;
  else if (path === '/resultados') page = <Navigate to="/panel" replace />;
  else if (path === '/estudio') page = <StudyPage />;
  else if (path === '/repaso') page = <StudyPage reviewOnly />;
  else if (path === '/cuenta' || path.startsWith('/cuenta/')) page = <AuthPage />;
  else if (path === '/panel') page = <DashboardPage />;
  else if (path === '/videos') page = <VideosPage />;
  else if (path === '/ajustes') page = <SettingsPage />;
  else if (path === '/flota') page = <FleetPage />;
  else if (path === '/precios') page = <PricingPage />;
  else if (path === '/compra') page = <PurchasePage />;
  else if (/^\/invitacion\/[^/]+$/.test(path)) page = <InvitationPage />;
  else if (path === '/conductores') page = <ProfileLandingPage profile="conductor" />;
  else if (path === '/autonomos') page = <ProfileLandingPage profile="autonomo" />;
  else if (path === '/flotas') page = <ProfileLandingPage profile="flota" />;
  else if (path === '/academias') page = <ProfileLandingPage profile="academia" />;
  else page = <NotFoundPage />;

  return (
    <AppLayout>
      <Suspense fallback={<LoadingPage />}>{page}</Suspense>
    </AppLayout>
  );
}
